/*
 *  ************************************************************************
 *  *  © [2015 - 2020] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React, { Suspense, lazy, useState, useRef } from "react";
import PropTypes from "prop-types";
import get from "lodash/get";
import { updateStoriesWithAlternatives } from "../../../utils";
import { Search } from "../../basic/search.js";
import { SvgHandler } from "../../svg-handler/svg-handler";
import { LOAD_MORE_LIMIT, STORY_FIELDS } from "../../../constants";
import omit from "@babel/runtime/helpers/objectWithoutProperties";
import { CustomLink } from "../../../components/custom-link";
import closeSvg from "../../../../assets/icons/social/close.svg";
import arrowIcon from "../../../../assets/icons/down-arrow.svg";
import loadingIcon from "../../../../assets/icons/loading.svg";
import "../../../../assets/stylesheets/search-page.scss";
import { SearchPageResults } from "./search-page-results-arrow";
import { connect } from "react-redux";

import "./search-page.m.css";
import { awaitHelper } from "@quintype/components";

const DatePicker = lazy(() => import("react-date-picker/dist/entry.nostyle"));
const SearchPageLayout = ({ config, builderConfig, data, stateConfig = {}, stateData = {} }) => {
  const { enableLocalization = false, localizedElements = {} } = get(
    stateConfig,
    ["pagebuilder-config", "general", "localization"],
    {}
  );

  const { searchLabels = {}, searchFilters = {} } = enableLocalization ? localizedElements : {};
  let {
    sortBy: localizedSortBy,
    relevance: localizedRelevance,
    newest: localizedNewest,
    message: localizedMessage,
    placeholder: localizedPlaceholder,
    title: localizedTitle,
  } = searchLabels;
  const {
    label: localizedLabel,
    section: localizedSection,
    author: localizedAuthor,
    storyType: localizedStoryType,
    date: localizedDate,
    clear: localizedClear,
    more: localizedMore,
    to: localizedTo,
  } = searchFilters;
  const sortOptions = [
    {
      name: localizedRelevance || "Relevance",
      filterKey: "score",
    },
    {
      name: localizedNewest || "Newest",
      filterKey: "latest-published",
    },
  ];

  const updatedData = data || stateData;
  const updatedConfig = config || stateConfig;

  const [stories, storiesHandler] = useState(updatedData.stories || []); // handle api stories
  const [offset, offSetHandler] = useState(updatedData.stories && updatedData.stories.length);
  const query = get(updatedData, ["params", "q"], "");

  if (!updatedData) return null;

  const pbConfig = builderConfig || get(updatedConfig, ["pagebuilder-config"], {});

  const intialLoadLimit = parseInt(
    get(pbConfig, ["search", "pageSetting", "searchResultsConfig", "initialLoadCount"], 6)
  );
  const [openPanel, handlePanel] = useState(null); // handle for template panel and author
  const [openDate, handleDatePanel] = useState(false); // date popup

  const [limit, handleLimit] = useState(intialLoadLimit);

  const [startDate, handleStartDate] = useState(0);
  const [endDate, handleEndDate] = useState(0);

  const [openDropdownPanel, toggleSortPanel] = useState(false); // to handle relevance and newest sort
  const [selectedSortOption, selectedSortOptionsHandler] = useState(get(sortOptions, [0, "name"])); // to set value of relevance and newest
  const [countStoryTemplate, handleCountStoryTemplate] = useState(7); // to show 3 more in filter list
  const [countAuthor, handleCountAuthor] = useState(7); // to show more in author filter
  const [countSection, handleCountSection] = useState(7); // to handle number of section names to show
  const [numberOfFilters, handleNumberOfFilters] = useState(0);
  const [hideLoadMore, hideLoadMoreHandler] = useState(stories && stories.length >= updatedData.total); // can be a conditional render
  const [openFilter, openFilterHandler] = useState(false);

  const selectedFilters = useRef({});
  const filterUrl = useRef("");
  const filterPanelRef = useRef(null);
  const { mountAt } = updatedConfig;

  const subsequentLoadLimit = parseInt(
    get(pbConfig, ["search", "pageSetting", "searchResultsConfig", "subsequentLoadCount"], LOAD_MORE_LIMIT)
  );

  const getLoadMore = async () => {
    const mountAtPrefix = mountAt || "";
    const queryString = `${mountAtPrefix}/api/v1/advanced-search?q=${query}&limit=${subsequentLoadLimit}${filterUrl.current}&offset=${offset}&fields=${STORY_FIELDS}`;

    const { data } = await awaitHelper((await fetch(queryString)).json());
    storiesHandler(updateStoriesWithAlternatives(stories.concat(data.items), false));

    offSetHandler(subsequentLoadLimit + offset);
    hideLoadMoreHandler(data.items && (data.items.length === 0 || data.items.length >= data.total));
    handleLimit(limit + subsequentLoadLimit);
  };

  const handleOpenPanel = () => {
    openFilterHandler(!openFilter);
    filterPanelRef.current.classList.toggle("close-panel");
  };

  const moreOptionsStoryTemplate = (event) => {
    event.preventDefault();
    event.stopPropagation();
    handleCountStoryTemplate(countStoryTemplate + 5);
  };

  const moreOptionsAuthor = (event) => {
    event.preventDefault();
    event.stopPropagation();
    handleCountAuthor(countAuthor + 5);
  };

  const moreOptionsSection = (event) => {
    event.preventDefault();
    event.stopPropagation();
    handleCountSection(countSection + 5);
  };

  const openFilterPanel = (event, index) => {
    event.stopPropagation();
    event.preventDefault();
    const activeIndex = openPanel === index ? null : index;
    handlePanel(activeIndex);
  };

  const opendatePanel = (event) => {
    event.stopPropagation();
    event.preventDefault();
    handleDatePanel(!openDate);
  };

  const handleSortChange = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.target.getAttribute("data-item") === "list-item") {
      const { name, filterKey } = JSON.parse(event.target.getAttribute("data-value"));
      selectedSortOptionsHandler(name);
      toggleSortPanel(false);
      setIfSelected(event, filterKey);
      filterUrl.current = generateUrl();
      getMeStories(filterUrl.current, mountAt);

      return;
    }
    toggleSortPanel(!openDropdownPanel);
  };

  const handleStartDateChange = (startDate) => {
    if (!startDate) {
      selectedFilters.current = omit(selectedFilters.current, ["published-after"]);
      filterUrl.current = generateUrl();
      getMeStories(filterUrl.current, mountAt);
      return;
    }
    selectedFilters.current = {
      ...selectedFilters.current,
      "published-after": { [startDate && startDate.getTime()]: true },
    };
    filterUrl.current = generateUrl();
    getMeStories(filterUrl.current, mountAt);
    handleStartDate(startDate);
    handleNumberOfFilters(numberOfFilters + 1);
  };

  const handleEndDateChange = (endDate) => {
    if (!endDate) {
      selectedFilters.current = omit(selectedFilters.current, ["published-before"]);
      filterUrl.current = generateUrl();
      getMeStories(filterUrl.current, mountAt);
      return;
    }
    selectedFilters.current = {
      ...selectedFilters.current,
      "published-before": { [endDate && endDate.getTime()]: true },
    };
    filterUrl.current = generateUrl();
    getMeStories(filterUrl.current, mountAt);
    handleEndDate(endDate);
    handleNumberOfFilters(numberOfFilters + 1);
  };

  const getFilterOptionName = (filterOption) => {
    switch (filterOption) {
      case "story-template":
        return localizedStoryType || "Story Type";
      case "author-name":
        return localizedAuthor || "Author";
      case "section-name":
        return localizedSection || "Section";
    }
  };

  const enableAdvancedSearch = get(pbConfig, ["search", "pageSetting", "enableAdvancedSearch"]);
  const getFilterElements = (filterOption, filterValue = {}) => {
    const filterKeys = (filterValue && Object.keys(filterValue)) || [];

    const hideMoreStoryTemplate = countStoryTemplate >= filterKeys.length ? "hide-more-btn" : "";
    const hideMoreAuthor = countAuthor >= filterKeys.length ? "hide-more-btn" : "";
    const hideMoreSection = countSection >= filterKeys.length ? "hide-more-btn" : "";
    switch (filterOption) {
      case "story-template":
        return (
          <div styleName="submenu-list">
            <label styleName="container">
              All
              <input type="radio" name="action" data-parent={"type"} value="ignore" />
              <span className="radio-checkmark" styleName="radio-checkmark" />
            </label>
            {filterKeys
              .sort()
              .slice(0, countStoryTemplate)
              .map((filterStoryKey, index) => (
                <div key={`radio-filter-${index}`}>
                  <label styleName="container">
                    {filterStoryKey.replace(/-/g, " ")}
                    <input type="radio" name="action" data-parent={"type"} value={filterStoryKey} />
                    <span className="radio-checkmark" styleName="radio-checkmark" />
                  </label>
                </div>
              ))}
            <div styleName={`more-option ${hideMoreStoryTemplate}`} onClick={moreOptionsStoryTemplate}>
              {filterKeys.length - countStoryTemplate} {localizedMore || "More"}
            </div>
          </div>
        );
      case "author-name":
        return (
          <div styleName="submenu-list">
            {filterKeys
              .sort()
              .slice(0, countAuthor)
              .map((filterAuthorKey, index) => (
                <div key={`check-filter-${index}`}>
                  <label className="container" styleName="container">
                    {filterAuthorKey}
                    <input type="checkbox" data-parent={"author"} value={filterAuthorKey} />
                    <span className="checkmark" styleName="checkmark" />
                  </label>
                </div>
              ))}
            <div styleName={`more-option ${hideMoreAuthor}`} onClick={(event) => moreOptionsAuthor(event)}>
              {filterKeys.length - countAuthor} {localizedMore || "More"}
            </div>
          </div>
        );
      case "section-name":
        return (
          <div styleName="submenu-list">
            {filterKeys
              .sort()
              .slice(0, countSection)
              .map((filterSectionKey, index) => (
                <div key={`check-filter-${index}`}>
                  <label className="container" styleName="container">
                    {filterSectionKey}
                    <input type="checkbox" data-parent={"section-name"} value={filterSectionKey} />
                    <span className="checkmark" styleName="checkmark" />
                  </label>
                </div>
              ))}
            <div
              data-test-id="More"
              styleName={`more-option ${hideMoreSection}`}
              onClick={(event) => moreOptionsSection(event)}
            >
              {filterKeys.length - countSection}
              {localizedMore || "More"}
            </div>
          </div>
        );
    }
  };
  const showDate = () => (
    <div>
      <div onClick={opendatePanel} styleName={openDate ? "panel-data border" : "panel-data"}>
        <div data-test-id="date" styleName="panel-date">
          {localizedDate || "Date"}
        </div>
        <span styleName={openDate ? "rotate-arrow-svg" : ""}>
          <SvgHandler xlinkHref={arrowIcon} width={"24px"} height={"24px"} />
        </span>
      </div>
      {openDate && (
        <div styleName="inner-date">
          <Suspense fallback={<SvgHandler xlinkHref={loadingIcon} width="12px" height="12px" />}>
            <DatePicker onChange={handleStartDateChange} value={startDate} />
          </Suspense>
          <span data-test-id="to" styleName="to-keyword">
            {localizedTo || "to"}
          </span>
          <Suspense fallback={<SvgHandler xlinkHref={loadingIcon} width="12px" height="12px" />}>
            <DatePicker onChange={handleEndDateChange} value={endDate} />
          </Suspense>
        </div>
      )}
    </div>
  );

  const setIfSelected = (event, customValue) => {
    const dataParent = event.target.getAttribute("data-parent");
    if (!dataParent) return null;
    if (event.target.value === "ignore") {
      selectedFilters.current = omit(selectedFilters.current, [dataParent]);
      return;
    }
    const initialSelectedValue = selectedFilters.current[dataParent] || {};
    const triggeredObject = {
      [event.target.value || customValue]: true,
    };
    const modifiedValue =
      event.target.getAttribute("type") === "checkbox"
        ? { ...initialSelectedValue, ...triggeredObject }
        : triggeredObject;

    selectedFilters.current = {
      ...selectedFilters.current,
      [dataParent]: modifiedValue,
    };
  };

  const setIfDeselected = (event) => {
    const dataParent = event.target.getAttribute("data-parent");
    if (!dataParent) return null;
    const initialSelectedValue = selectedFilters.current[dataParent] || {};
    event.target.value in initialSelectedValue && delete initialSelectedValue[event.target.value];
    if (Object.keys(initialSelectedValue).length === 0) {
      selectedFilters.current = omit(selectedFilters.current, [dataParent]);
    } else {
      selectedFilters.current = {
        ...selectedFilters.current,
      };
    }
  };

  const generateUrl = () => {
    return Object.keys(selectedFilters.current).reduce((acc, selectedFilter) => {
      const objKeys = Object.keys(selectedFilters.current[selectedFilter]);
      if (objKeys.length) {
        const selectedKeys = objKeys.join(",");
        acc += `&${selectedFilter}=${selectedKeys}`;
      }
      return acc;
    }, "");
  };

  // handle event delegation
  const handleFilterClicks = (event) => {
    event.stopPropagation();
    if (event.target.checked) {
      setIfSelected(event);
    } else {
      setIfDeselected(event);
    }
    filterUrl.current = generateUrl();
    getMeStories(filterUrl.current, mountAt);
  };

  const getMeStories = async (url, mountAt) => {
    try {
      const mountAtPrefix = mountAt || "";
      const queryString = `${mountAtPrefix}/api/v1/advanced-search?q=${query}${url}&limit=${limit}&fields=${STORY_FIELDS}`;
      const { data } = await awaitHelper((await fetch(queryString)).json());
      storiesHandler(data.items);
      hideLoadMoreHandler(data.items.length >= data.total);
    } catch (error) {
      console.log(error);
    }
  };

  const getNumberOfFilters = () => {
    const flag = selectedFilters.current.sort ? -1 : 0;
    const filterNumber =
      selectedFilters.current &&
      Object.values(selectedFilters.current).reduce((acc, selectedFilter) => {
        return acc + Object.keys(selectedFilter).length;
      }, flag);
    if (filterNumber === 0) return null;
    return <span styleName="filter">{filterNumber}</span>;
  };

  const getPbFilterOptions = () => {
    const filterOptions = get(updatedData, ["facets"], {});
    const pbFilterOptions = get(pbConfig, ["search", "pageSetting", "filterOptions"], {});
    const updatedFilterOptions = {};
    Object.keys(pbFilterOptions).forEach((pbFilterOption) => {
      if (pbFilterOption !== "date" && pbFilterOptions[pbFilterOption]) {
        updatedFilterOptions[pbFilterOption] = filterOptions[pbFilterOption];
      }
    });
    return updatedFilterOptions;
  };

  const filterOption = (data) => {
    const filterOptions = getPbFilterOptions();

    return (
      <div onClick={handleFilterClicks}>
        {Object.keys(filterOptions).map((filterOption, index) => (
          <>
            <div
              key={index}
              styleName={`panel-data ${index === openPanel ? "border" : ""}`}
              onClick={(event) => openFilterPanel(event, index)}
            >
              <div data-test-id="filter-option" styleName="panel-option-name">
                {getFilterOptionName(filterOption)}
              </div>
              <span styleName={index === openPanel ? "rotate-svg" : ""}>
                <SvgHandler xlinkHref={arrowIcon} width={"24px"} height={"24px"} />
              </span>
            </div>
            <div styleName={`sub-list ${index === openPanel ? "open" : ""}`}>
              {getFilterElements(filterOption, filterOptions[filterOption])}
            </div>
          </>
        ))}
      </div>
    );
  };

  const isDateEnable = get(pbConfig, ["search", "pageSetting", "filterOptions", "date"], true);

  const clearBtn = Object.keys(selectedFilters.current).filter(
    (selectedFilter) => !("score" in selectedFilters.current[selectedFilter])
  ).length;
  const isFilterEnable = enableAdvancedSearch ? "update-height" : "";

  const noStoriesMsg = () => {
    if (localizedMessage) {
      const shortCodeMapping = [{ code: "[query]", value: query }];
      // eslint-disable-next-line array-callback-return
      shortCodeMapping.map((item) => {
        localizedMessage = localizedMessage.replace(item.code, item.value);
      });
      return <p styleName="no-more-result">{localizedMessage}</p>;
    }
    return (
      <p styleName="no-more-result">
        No stories found for &#39;<strong>{query}</strong>&#39;.
      </p>
    );
  };

  return (
    <div styleName="page-wrapper">
      <h1 styleName="title">{localizedTitle || "Search Results"}</h1>
      <div className="component-wrapper" styleName="search-page-wrapper">
        <div styleName={`story-list ${isFilterEnable}`}>
          <div>
            <Search q={query} localizedPlaceholder={localizedPlaceholder} />
            {enableAdvancedSearch && (
              <div styleName="header-option">
                <p data-test-id="Sort-by">{localizedSortBy || "Sort by"}</p>

                <div styleName="dropdownWrapper">
                  <div>
                    <div
                      data-test-id="sort-dropdown"
                      data-item="list"
                      styleName="select-wrapper"
                      onClick={handleSortChange}
                    >
                      <div styleName={openDropdownPanel ? "placeholder select-dropdown" : "placeholder"}>
                        {selectedSortOption}
                      </div>
                      {openDropdownPanel && (
                        <ul styleName="select">
                          {sortOptions.map((item, index) => (
                            <li
                              styleName="list"
                              key={`dropdown-${index}`}
                              data-value={JSON.stringify(item)}
                              data-parent={"sort"}
                              data-item="list-item"
                              data-test-id="sort-by-items"
                            >
                              {item.name}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
                <div onClick={handleOpenPanel} styleName={openFilter ? "filter-btn selected-filter" : "filter-btn"}>
                  <span data-test-id="filter-button" styleName="filter-btn-wrapper">
                    {localizedLabel || "Filter"}
                    {getNumberOfFilters()}
                  </span>
                </div>
              </div>
            )}
          </div>
          <div styleName="stories">
            {stories.length ? (
              <SearchPageResults
                collection={{
                  ...updatedData.collectionMetadata,
                  items: stories.map((story) => ({ id: story.id, type: "story", story })),
                }}
                config={updatedConfig}
                getMoreStories={() => getLoadMore()}
                hideLoadmore={hideLoadMore}
                limit={limit}
                builderConfig={builderConfig}
              />
            ) : (
              <div className="component-wrapper">{noStoriesMsg()}</div>
            )}
          </div>
        </div>

        {enableAdvancedSearch && (
          <div ref={filterPanelRef} styleName="full-height-panel" id="panel">
            <div styleName="close-wrapper" onClick={handleOpenPanel}>
              <SvgHandler xlinkHref={closeSvg} clazzName="close" width="24px" height="24px" />
            </div>
            <div styleName="clear-filter" data-test-id="filter-label">
              {localizedLabel || "Filter"}
              <div styleName="clear-wrapper">
                {clearBtn === 0 ? null : (
                  <CustomLink href={`search?q=${query}`} styleName="clear-btn" data-test-id="clear-filter">
                    {localizedClear || "Clear"}
                  </CustomLink>
                )}
              </div>
            </div>
            <div>
              {filterOption(updatedData)}
              {isDateEnable && showDate()}
              <div onClick={handleOpenPanel} styleName="filter-wrapper">
                <button styleName="save-btn">Search</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

SearchPageLayout.propTypes = {
  data: PropTypes.object,
  config: PropTypes.object,
  stateConfig: PropTypes.object,
  stateData: PropTypes.object,
  builderConfig: PropTypes.object,
};

function mapStateToProps(state) {
  return {
    stateConfig: get(state, ["qt", "config"], {}),
    stateData: get(state, ["qt", "data"], {}),
  };
}

export default connect(mapStateToProps)(SearchPageLayout);
